// ============================================
// Auth Store (Zustand)
// 인증 및 사용자 상태 관리
// ============================================

import { createStore } from 'zustand/vanilla';

export const useAuthStore = createStore((set, get) => ({
  // ============================================
  // State
  // ============================================

  // Firebase 인스턴스
  auth: null,
  storage: null,
  db: null,

  // 현재 사용자
  currentUser: null,
  userCarbon: 0,
  userNickname: '',

  // 프로필 캐시
  cachedProfiles: [],
  cachedPrimaryProfileId: null,

  // 인증 상태
  isAuthenticated: false,
  isAuthLoading: true,

  // ============================================
  // Actions - Firebase
  // ============================================

  setAuth: (authInstance) => set({ auth: authInstance }),

  setStorage: (storageInstance) => set({ storage: storageInstance }),

  setDb: (dbInstance) => set({ db: dbInstance }),

  // ============================================
  // Actions - User
  // ============================================

  setCurrentUser: (user) => set({
    currentUser: user,
    isAuthenticated: !!user,
    isAuthLoading: false
  }),

  setUserCarbon: (carbon) => set({ userCarbon: carbon }),

  setUserNickname: (nickname) => set({ userNickname: nickname }),

  // ============================================
  // Actions - Profiles
  // ============================================

  setCachedProfiles: (profiles) => set({ cachedProfiles: profiles }),

  setCachedPrimaryProfileId: (profileId) => set({ cachedPrimaryProfileId: profileId }),

  addProfile: (profile) => set((state) => ({
    cachedProfiles: [...state.cachedProfiles, profile]
  })),

  updateProfile: (profileId, updates) => set((state) => ({
    cachedProfiles: state.cachedProfiles.map(p =>
      p.id === profileId ? { ...p, ...updates } : p
    )
  })),

  removeProfile: (profileId) => set((state) => ({
    cachedProfiles: state.cachedProfiles.filter(p => p.id !== profileId)
  })),

  // ============================================
  // Selectors
  // ============================================

  getPrimaryProfile: () => {
    const { cachedProfiles, cachedPrimaryProfileId } = get();

    if (!cachedPrimaryProfileId) {
      if (cachedProfiles.length > 0) {
        const sorted = [...cachedProfiles].sort((a, b) => {
          const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return aTime - bTime;
        });
        return sorted[0];
      }
      return null;
    }
    return cachedProfiles.find(p => p.id === cachedPrimaryProfileId) || null;
  },

  getProfileById: (profileId) => {
    return get().cachedProfiles.find(p => p.id === profileId) || null;
  },

  // ============================================
  // Actions - Auth Flow
  // ============================================

  logout: () => set({
    currentUser: null,
    userCarbon: 0,
    userNickname: '',
    cachedProfiles: [],
    cachedPrimaryProfileId: null,
    isAuthenticated: false
  }),

  // ============================================
  // Actions - Reset
  // ============================================

  reset: () => set({
    currentUser: null,
    userCarbon: 0,
    userNickname: '',
    cachedProfiles: [],
    cachedPrimaryProfileId: null,
    isAuthenticated: false,
    isAuthLoading: false
  })
}));

export default useAuthStore;
